import { DroneStatusType } from './drone-status-type';

export interface DroneStatusDisplay {
	/** Human-readable label shown to operators. */
	label: string;
	/** MUI theme color token, e.g. 'success.main'. */
	color: string;
}

/**
 * Single source of truth for how a drone status is shown (label + badge color).
 * Full Record — TS forces an entry for every DroneStatusType, so a new status
 * cannot be added to the enum without deciding how it renders.
 */
export const DRONE_STATUS_DISPLAY: Record<DroneStatusType, DroneStatusDisplay> = {
  [DroneStatusType.IDLE]: { label: 'Idle', color: 'success.main' },
  [DroneStatusType.NOT_ACTIVE]: { label: 'Not active', color: 'text.secondary' },
  [DroneStatusType.ACTIVE]: { label: 'Active', color: 'success.main' },
  [DroneStatusType.STANDBY]: { label: 'Standby', color: 'text.secondary' },
  [DroneStatusType.ASSIGNED]: { label: 'Assigned', color: 'info.main' },
  [DroneStatusType.SENT_FOR_AUTHORIZATION]: { label: 'Sent for authorization', color: 'warning.main' },
  [DroneStatusType.AUTHORIZED]: { label: 'Authorized', color: 'success.main' },
  [DroneStatusType.NOT_AUTHORIZED]: { label: 'Not authorized', color: 'warning.main' },
  [DroneStatusType.MISSION]: { label: 'Mission', color: 'info.main' },
  [DroneStatusType.BREAK]: { label: 'Break', color: 'text.secondary' },
  [DroneStatusType.RTL]: { label: 'Return to launch', color: 'warning.main' },
  [DroneStatusType.LAND]: { label: 'Landing', color: 'info.main' },
  [DroneStatusType.NOT_USED]: { label: 'Not used', color: 'text.secondary' },
  [DroneStatusType.ERROR]: { label: 'Error', color: 'error.main' },
  [DroneStatusType.OFFLINE]: { label: 'Offline', color: 'text.secondary' },
  [DroneStatusType.EMERGENCY]: { label: 'Emergency', color: 'error.main' },
  [DroneStatusType.ARCHIVE]: { label: 'Archived', color: 'text.secondary' },
  // Firmware (flightdispatcher DroneStates) statuses:
  [DroneStatusType.UPDATE_SOFTWARE_REQUIRED]: { label: 'Update required', color: 'warning.main' },
  [DroneStatusType.UPLOAD]: { label: 'Uploading mission', color: 'info.main' },
  [DroneStatusType.MISSION_COMPLETE]: { label: 'Mission complete', color: 'success.main' },
  [DroneStatusType.CLOSE]: { label: 'Closing', color: 'text.secondary' },
  [DroneStatusType.EMERGENCY_LAND]: { label: 'Emergency landing', color: 'error.main' },
  [DroneStatusType.MANUAL_CONTROL]: { label: 'Manual control', color: 'info.main' },
};

const FALLBACK_COLOR = 'text.secondary';

/**
 * Safe lookup: accepts any raw status string. Unknown values (e.g. a status a
 * newer firmware sends that this build predates) fall back to the raw string
 * with a neutral color instead of rendering blank or throwing.
 */
export const getDroneStatusDisplay = (status?: string | null): DroneStatusDisplay =>
  (status ? DRONE_STATUS_DISPLAY[status as DroneStatusType] : undefined) ?? {
    label: status ?? '',
    color: FALLBACK_COLOR,
  };
